import { Group } from './Group';
import { Media } from './Media';
import { User } from './User';

export type Participant = {
  id: string;
  user: User;
};

export type EventApprovalStatus = 'pending' | 'approved' | 'rejected';
export enum EventGameStyle {
  TEAM = 'team',
  INDIVIDUAL = 'individual',
}
export const EventGameStyleLabels: Record<EventGameStyle, string> = {
  [EventGameStyle.TEAM]: 'Team',
  [EventGameStyle.INDIVIDUAL]: 'Individual',
};

export type EventPrivacyType = 'public' | 'private';
export enum CompetitionFormat {
  LEAGUE_PLAY = 'league_play',
  LEAGUE_TO_SINGLE_ELIMINATION = 'league_play_to_single_elimination_bracket',
  SINGLE_ELIMINATION = 'tournament_single_elimination_bracket',
  DOUBLE_ELIMINATION = 'tournament_double_elimination_bracket',
  ROUND_ROBIN = 'tournament_round_robin',
  ROUND_ROBIN_TO_SINGLE = 'tournament_round_robin_to_single_elimination_bracket',
  PLAYERS_VOTE = 'tournament_players_vote',
  HOSTS_VOTE = 'tournament_hosts_vote',
  BEST_SCORE = 'tournament_best_score',
  SINGLE_ELIMINATION_WITH_CONSOLATION = 'tournament_single_elimination_with_consolation_bracket',
  DOUBLE_ELIMINATION_WITH_CONSOLATION = 'tournament_double_elimination_with_consolation_bracket',
}
export const CompetitionFormatLabels: Record<CompetitionFormat, string> = {
  [CompetitionFormat.LEAGUE_PLAY]: 'League Play',
  [CompetitionFormat.LEAGUE_TO_SINGLE_ELIMINATION]: 'League to Single Elimination',
  [CompetitionFormat.SINGLE_ELIMINATION]: 'Single Elimination Bracket',
  [CompetitionFormat.DOUBLE_ELIMINATION]: 'Double Elimination Bracket',
  [CompetitionFormat.ROUND_ROBIN]: 'Round Robin',
  [CompetitionFormat.ROUND_ROBIN_TO_SINGLE]: 'Round Robin to Single Elimination',
  [CompetitionFormat.PLAYERS_VOTE]: "Players' Vote",
  [CompetitionFormat.HOSTS_VOTE]: "Hosts' Vote",
  [CompetitionFormat.BEST_SCORE]: 'Best Score',
  [CompetitionFormat.SINGLE_ELIMINATION_WITH_CONSOLATION]: 'Single Elimination with Consolation',
  [CompetitionFormat.DOUBLE_ELIMINATION_WITH_CONSOLATION]: 'Double Elimination with Consolation',
};

export type BracketGenerationMethod = 'seeding_with_round_robin' | 'random_draw' | 'manual_seed';

export enum CashBreakdownType {
  WINNER_TAKES_ALL = 'winner_takes_all',
  TIER_70_20_10 = 'tiered_70_20_10',
  TIER_60_25_15 = 'tiered_60_25_15',
  TIER_50_30_20 = 'tiered_50_30_20',
}

export type EventCategory = {
  id: string;
  name: string;
  emoji: string;
  sequence: number;
  created: string;
  modified: string;
};

export enum EventInviteStatus {
  PENDING = 'pending',
  GOING = 'going',
  DECLINED = 'declined',
}

export type Event = {
  id: string;
  name: string;
  description?: string;
  announcement?: string;
  coverMediaId?: string;
  hostUserId: string;
  eventCategory: EventCategory;
  venueName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  timezone?: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  gameStyle: EventGameStyle;
  privacyType: EventPrivacyType;
  competitionFormat: CompetitionFormat;
  bracketGenerationMethod: BracketGenerationMethod;
  charity: boolean;
  minSkillLevel: number;
  maxSkillLevel: number;
  maxTeams?: number;
  maxPlayersPerTeam?: number;
  numberOfRounds?: number;
  matchupsPerRound?: number;
  matchFrequency: string;
  hostPercentageFee: number;
  entryFee: number;
  cashBreakdownType: CashBreakdownType;
  start: string; // ISO string timestamp
  startUtc: string;
  end: string;
  endUtc: string;
  registrationDeadline: string;
  registrationDeadlineUtc: string;
  approvalStatus: EventApprovalStatus;
  approvalStatusTimestamp: string;
  published: boolean;
  featured: boolean;
  deleted: boolean;
  created: string;
  modified: string;
  coverMedia?: Media;
  hostUser?: User;
  maxPrize?: number;
  eventSchedules?: EventSchedules[];
  fullAddress?: string;
  isFinished?: boolean;
  hostJustification?: string;
  participants: Participant[];
  totalParticipants: number;
  rsvpStatus?: EventInviteStatus;
  hostRating?: number;
  groups?: Group[];
  isMatchesPublished: boolean;
};

export type EventSchedules = {
  id: string;
  round?: string;
  startDatetimeFrequency?: string;
  startDatetimeFrequencyUtc?: string;
  endDatetimeFrequency?: string;
  endDatetimeFrequencyUtc?: string;
  timezone?: string;
};

export type CreateEventDto = {
  name: string;
  description: string;
  announcement?: string;
  coverMediaId?: string;
  eventCategoryId: string;

  venueLocation?: {
    name: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
    latitude: number;
    longitude: number;
    fullAddress?: string;
  };

  gameStyle: EventGameStyle;
  privacyType: EventPrivacyType;
  competitionFormat: CompetitionFormat;

  bracketGenerationMethod?: string | null;

  charity: boolean;
  minSkillLevel: number;
  maxSkillLevel: number;

  maxTeams?: number;
  maxPlayersPerTeam?: number;
  maxPrize: number;
  numberOfRounds?: number;
  matchupsPerRound?: number;

  // matchFrequency: string; // comma-separated values

  hostPercentageFee: number;
  entryFee: number;
  cashBreakdownType: CashBreakdownType;

  registrationDeadline: Date;
  start: Date;
  end: Date;
  timezone: string;

  schedules?: Partial<Pick<EventSchedules, 'round' | 'startDatetimeFrequency' | 'endDatetimeFrequency'>[]>;
  hostJustification?: string;
};
